import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  FormWithSimpleMaterializedFormsFragment,
  TrainingFormWithTrainingDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formFromData, useCreateForm } from 'modules/forms/hooks/useForms'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { Form } from 'modules/trainings/components/Revamp/Form'

import EditTrainingFormModal from './EditTrainingForm'

const TrainingFormDetail = () => {
  const { id, formId } = useParams<{ id: string; formId: string }>()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { company, access } = useCurrentSession()
  const { data, loading, error } = useQuery(TrainingFormWithTrainingDocument, {
    variables: { id: formId ?? '', trainingId: Number(id), companyId: company.id },
  })

  const trainingForm: FormWithSimpleMaterializedFormsFragment | undefined =
    data?.forms.form ?? undefined
  const [openSidepanel, setOpenSidepanel] = useState<boolean>(false)
  const [openEditTrainingFormModal, setOpenEditTrainingFormModal] = useState<boolean>(false)

  const { createForm } = useCreateForm()
  const onSaveAsTemplate = async (formToSave: FormWithSimpleMaterializedFormsFragment) => {
    if (formToSave) {
      const formElements = formToSave?.elements ?? []
      // setting the 'resourceId' to null makes the form to be saved as template
      const formInstance = formFromData({
        ...formToSave,
        description: formToSave.description ?? undefined,
        resourceId: undefined,
        status: 'active',
        elements: formElements.map((element) => ({
          ...element,
          description: element.description ?? '',
        })),
      })
      const result = await createForm(formInstance, company.id, access.id)
      if (result.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('trainings.surveys.create.dropdown.template_success'),
        })
      } else {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('trainings.surveys.create.dropdown.template_error'),
        })
      }
      return result
    }
  }

  const handleSend = () => {
    const trainingStatus = data?.trainings?.training?.status || 'draft'

    if (trainingStatus === 'active') {
      navigate(
        navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.sendSurvey.fullPath(
          {
            id: String(id),
            formId: String(formId),
          }
        )
      )
    } else {
      navigate(
        navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.blockedSend.fullPath(
          {
            id: String(id),
            formId: String(formId),
          }
        )
      )
    }
  }

  const handleActionClick = (key: string): void => {
    switch (key) {
      case 'send':
        handleSend()
        break
      case 'edit':
        const editPath =
          navigation.trainings.list.details_revamp.trainingSurveyDetail.edit.fullPath({
            id: String(id),
            formId: String(formId),
          })
        navigate(editPath)
        break
      case 'delete':
        navigate(
          navigation.trainings.list.details_revamp.trainingSurveyDetail.questions.delete.fullPath({
            id: String(id),
            formId: String(formId),
          })
        )
        break
      case 'edit_settings':
        setOpenEditTrainingFormModal(true)
        break
      case 'save_template':
        if (trainingForm) {
          onSaveAsTemplate(trainingForm)
        }
        break
    }
  }

  return (
    <ResponsivePage.Body
      loading={loading}
      error={error}
      variant="fullscreen"
      onActionClick={handleActionClick}
    >
      <Form
        openSidepanel={openSidepanel}
        setOpenSidepanel={setOpenSidepanel}
        trainingForm={trainingForm}
        trainingId={Number(id)}
      />
      {trainingForm && (
        <EditTrainingFormModal
          onClose={() => setOpenEditTrainingFormModal(false)}
          onSuccess={() => {
            setOpenEditTrainingFormModal(false)
            app.setFlash({
              type: 'correct',
              message: i18n.t('trainings.surveys.create.edit_modal.form_success_message'),
            })
          }}
          onError={() => {
            setOpenEditTrainingFormModal(false)
            app.setFlash({
              type: 'wrong',
              message: i18n.t('trainings.surveys.create.edit_modal.form_error_message'),
            })
          }}
          isOpen={openEditTrainingFormModal}
          trainingForm={trainingForm}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default TrainingFormDetail
